import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import {
    authenticationReferenceMethodOptions,
    azureCloudTypeMetadata,
    azureResourceSelectMethodOptions,
    cdcDestination,
    connectionStringParseControl,
    sqlAdditionalPropertiesKeyOptions,
    sqlAdditionalPropertiesInFabricKeyOptions,
    sqlEncryptControl,
    sqlEncryption,
    sqlTrustServerCertificateControl
} from '../consts/metadata-consts';
import { azureSqlDomain, sqlPEWellknownErrorMessages } from 'app/shared/utils/connector-metadata-static-consts';
import { isDevEnvironment, isCIEnvironment } from 'environments/environment.util';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, CredentialTypeOptions, SQLAuthenticationType } from 'shell-models-lib';
import {
    azureDefaultAuthRefTypes,
    defaultSqlAuthenticationOptionAKV,
    defaultSqlAuthenticationOptionNoAKV,
    mergeUamiCredentialOptionSql,
    mergeUserAuthCredentialOption
} from '../utls/authenticationUtils';
import { GlobalVariables } from 'app/services/globals';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { compact } from 'lodash';

const tenantInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    }
};

const servicePrincipalIdInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    }
};

const akvSwitcherInput: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            validators: [{ type: 'required', errorMessage: '' }]
        }
    },
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureSql_DisplayText),
        stringResources.ModelName_ServicePrincipalKey
    )
};

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function azureSqlMiAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        nested: 1,
        bind: 'authReferenceMethod',
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? [
                            // service Principal
                            tenantInput,
                            servicePrincipalIdInput,
                            akvSwitcherInput,
                            azureCloudTypeMetadata
                        ]
                        : [
                            // system Assigned Managed Identity
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureSql_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-managed-instance?tabs=data-factory#managed-identity'
                            }
                        ]
            },
            {
                caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        supportFic: GlobalVariables.supportFic,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

const dataflowAzureSqlMI: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.SqlMI,
    displayName: stringResources.AzureSQLManagedInstance_DisplayText,
    settings: adfModelTypeMap.azureSqlMI.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true,
    enableAlterRow: true
};

const accountSelectionControl: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AccountSelectionMethodLabel,
    description: stringResources.AzureSql_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    defaultValue: '{serverName != null || parameterized ? "manual" : "azure"}',
    isLabelVertical: true,
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-sql-db-select',
                    bind: {
                        fullyQualifiedDomainName: 'serverName',
                        databaseName: 'databaseName',
                        serverResourceId: '_serverResourceID'
                    },
                    isSqlDW: false,
                    isSqlMI: true,
                    showPEStatus: true,
                    resourceType: 'Microsoft.Sql/managedInstances',
                    listDBApiVersion: '2020-08-01-preview',
                    validators: {
                        serverName: [{ type: 'required', errorMessage: '' }],
                        databaseName: [{ type: 'required', errorMessage: '' }]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'serverName',
                        label: stringResources.FullyServerNameLabel,
                        required: true,
                        placeholder: `e.g., myserver.${azureSqlDomain}`,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable',
                    attribute: 'databaseName',
                    child: {
                        type: 'textbox',
                        bind: 'databaseName',
                        label: stringResources.ModelName_DatabaseName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'private-endpoint-status'
                }
            ]
        }
    ]
};

const azureSqlMIAuthControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: mergeUserAuthCredentialOption(
        mergeUamiCredentialOptionSql(defaultSqlAuthenticationOptionNoAKV),
        SQLAuthenticationType.UserAuth
    ),
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    defaultValue: SQLAuthenticationType.SQL,
    cases: [
        {
            caseValue: SQLAuthenticationType.SQL,
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'userName',
                        label: stringResources.ModelName_UserName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    hideAKVSwitcherInConnStrProps: true,
                    bind: 'password',
                    label: stringResources.ModelName_Password,
                    child: {
                        type: 'parameterizable',
                        attribute: 'password',
                        child: {
                            type: 'password',
                            label: stringResources.ModelName_Password,
                            required: true,
                            isLabelVertical: true,
                            validators: [{ type: 'required', errorMessage: '' }]
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.AzureSql_DisplayText
                            ),
                            stringResources.ModelName_Password
                        )
                    }
                }
            ]
        },
        {
            caseValue: SQLAuthenticationType.ServicePrincipal,
            children: [azureSqlMiAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
        },
        {
            caseValue: SQLAuthenticationType.ManagedServiceIdentity,
            children: !GlobalVariables.isSamiSupportEnabled
                ? [
                    {
                        type: 'management-identity-info',
                        resourceType: stringResources.AzureSQLManagedInstance_DisplayText,
                        link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-managed-instance?tabs=data-factory#managed-identity'
                    }
                ]
                : [azureSqlMiAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
        },
        {
            caseValue: SQLAuthenticationType.UserAssignedManagedIdentity,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        },
        {
            caseValue: SQLAuthenticationType.UserAuth,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    hide: !GlobalVariables.enableConnectorUserAuthCred,
                    filterCredentialType: CredentialTypeOptions.UserAuth,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        }
    ]
};

const azureSqlMIV1LinkedServiceControl: ControlMetadata = {
    type: 'select-button-switch',
    required: true,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.ModelName_ConnectionString,
            value: false
        },
        {
            label: stringResources.AzureKeyVault_DisplayText,
            value: true,
            hideAKVOptionIfNotChosen: false
        }
    ],
    bind: 'enableAKV',
    isNotRelatedToCredential: true,
    defaultValue: false,
    cases: [
        {
            caseValue: true,
            children: [
                {
                    type: 'azure-key-vault',
                    enableAKVParameterization: true,
                    bind: 'referedAKV',
                    isLabelVertical: true,
                    description: format(
                        stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureSql_DisplayText),
                        // eslint-disable-next-line max-len
                        'Server=tcp:<servername>.database.windows.net,1433;Database=<databasename>;User ID=<username>@<servername>;Password=<password>;Trusted_Connection=False;Encrypt=True;Connection Timeout=30'
                    )
                },
                {
                    type: 'select-switch',
                    bind: 'authenticationType',
                    label: stringResources.ModelName_AuthenticationType,
                    allowNull: false,
                    options: mergeUserAuthCredentialOption(
                        mergeUamiCredentialOptionSql(defaultSqlAuthenticationOptionAKV),
                        SQLAuthenticationType.UserAuth
                    ),
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }],
                    isLabelVertical: true,
                    defaultValue: SQLAuthenticationType.SqlOrManagedServiceIdentity,
                    cases: [
                        {
                            caseValue: SQLAuthenticationType.ServicePrincipal,
                            children: [azureSqlMiAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
                        },
                        {
                            caseValue: SQLAuthenticationType.SqlOrManagedServiceIdentity,
                            children: [azureSqlMiAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
                        },
                        {
                            caseValue: SQLAuthenticationType.UserAssignedManagedIdentity,
                            children: [
                                {
                                    type: 'credential-select',
                                    bind: 'credential',
                                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }]
                                }
                            ]
                        },
                        {
                            caseValue: SQLAuthenticationType.UserAuth,
                            children: [
                                {
                                    type: 'credential-select',
                                    bind: 'credential',
                                    hide: !GlobalVariables.enableConnectorUserAuthCred,
                                    filterCredentialType: CredentialTypeOptions.UserAuth,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }]
                                }
                            ]
                        }
                    ]
                },
                sqlEncryption(stringResources.AzureSQLManagedInstance_DisplayText)
            ]
        },
        {
            caseValue: false,
            children: [
                accountSelectionControl,
                azureSqlMIAuthControl,
                sqlEncryption(stringResources.AzureSQLManagedInstance_DisplayText),
                {
                    type: 'key-val-list',
                    bind: 'additionalFields',
                    label: stringResources.AdditionalConnectionStringFields
                }
            ]
        }
    ]
};

export const azureSqlMIMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureSqlMI.linkedServiceType,
        displayName: stringResources.AzureSQLManagedInstance_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-azure_sql_db_managed_instance'
        },
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureSqlMI' as ResourceKind
        },
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.Basic,
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.ServicePrincipal,
            GlobalVariables.enableWorkspaceIdentity ? ConnectionAuthKind.WorkspaceIdentity : undefined
        ]),
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        copyJob: { omitConnectionDatabaseFromDataset: true },
        cdc: {
            supportCDC: true,
            supportedCDCDestinations: cdcDestination,
            tableIcon: {
                regular: 'icon-table_text_cdc_20_regular',
                filled: 'icon-table_text_cdc_20_filled'
            },
            GA: true
        },
        supportMultipleIncrementalColumns: true,
        supportScriptActivity: true,
        supportStoredProcedureActivity: true,
        distinguishTableView: true,
        queryAlias: 'sqlReaderQuery',
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        supportOPDG: GlobalVariables.enableOPDG,
        supportOPDGInMirror: true,
        supportVNETInMirror: true,
        dataflowSource: [dataflowAzureSqlMI],
        dataflowSink: [dataflowAzureSqlMI],
        isFirstClass: true,
        supportPE: isDevEnvironment() || isCIEnvironment(),
        peGroupIDs: ['managedInstance'],
        peWellknowErrorMessages: sqlPEWellknownErrorMessages,
        resourceTypes: ['Microsoft.Sql/managedInstances'],
        supportPreCopyScript: true,
        supportDatasetV2: true,
        supportGetMetadataActivity: true,
        dataflowSinkStagingSchema: true,
        tableColumnNameQuotes: ['[', ']'],
        copySinkTypes: [adfModelTypeMap.azureSqlMI.copySinkType],
        copySourceTypes: [adfModelTypeMap.azureSqlMI.copySourceType],
        category: ['Azure'],
        supportFirstClassParameterExperience: true,
        supportControlTable: true,
        additionalPropertiesInFabric: GlobalVariables.connectionPropertiesForSql ? sqlAdditionalPropertiesInFabricKeyOptions : undefined,
        controls: [
            {
                type: 'radio-switch',
                bind: 'sqlVersion',
                label: stringResources.VersionLabel,
                isLabelVertical: true,
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                cases: [
                    {
                        caseValue: driverVersions.v1,
                        children: [azureSqlMIV1LinkedServiceControl]
                    },
                    {
                        caseValue: driverVersions.v2,
                        children: [
                            connectionStringParseControl(true),
                            accountSelectionControl,
                            azureSqlMIAuthControl,
                            sqlEncryption(stringResources.AzureSql_DisplayText),
                            sqlEncryptControl,
                            sqlTrustServerCertificateControl,
                            {
                                type: 'parameter-config',
                                bind: 'parameterizableAdditionalFields',
                                label: stringResources.AdditionalConnectionStringFields,
                                isLayoutVertical: true,
                                isKeyDropdown: true,
                                keyOptions: sqlAdditionalPropertiesKeyOptions,
                                keyOptionHasType: true,
                                dynamicInputTypes: true,
                                dynamicInputTypesParameterizable: true,
                                keyLabel: stringResources.PropertyName,
                                defaultWidth: 250
                            }
                        ]
                    }
                ]
            }
        ]
    };

    return metadata;
};
